import { Task } from '../types/task';

/**
 * 任务时间排序工具
 * 提供统一的任务排序逻辑，适用于所有页面和组件
 */

export interface TaskTimeInfo {
  primaryTime: Date | null;    // 主要时间（用于排序）
  secondaryTime: Date | null;  // 次要时间（用于辅助排序）
  timeType: 'start' | 'due' | 'none'; // 时间类型
  hasTime: boolean;            // 是否有时间信息
}

/**
 * 获取任务的时间信息
 * 优先级：startAt > dueAt > 无时间
 */
export const getTaskTimeInfo = (task: Task): TaskTimeInfo => {
  const startAt = task.startAt ? new Date(task.startAt) : null;
  const dueAt = task.dueAt ? new Date(task.dueAt) : null;

  // 优先使用 startAt
  if (startAt) {
    return {
      primaryTime: startAt,
      secondaryTime: dueAt, // dueAt 作为次要排序依据
      timeType: 'start',
      hasTime: true
    };
  }

  // 其次使用 dueAt
  if (dueAt) {
    return {
      primaryTime: dueAt,
      secondaryTime: null,
      timeType: 'due',
      hasTime: true
    };
  }

  // 无时间信息
  return {
    primaryTime: null,
    secondaryTime: null,
    timeType: 'none',
    hasTime: false
  };
};

/**
 * 比较两个任务的时间
 * 返回值：-1 表示 a 在前，1 表示 b 在前，0 表示相等
 */
export const compareTaskTime = (a: Task, b: Task, temporaryVisibleTasks?: Set<string>): number => {
  const timeA = getTaskTimeInfo(a);
  const timeB = getTaskTimeInfo(b);

  // 0. 临时显示状态检查（如果提供了临时任务列表）
  if (temporaryVisibleTasks) {
    const aIsTemporary = temporaryVisibleTasks.has(a.id);
    const bIsTemporary = temporaryVisibleTasks.has(b.id);

    // 如果一个是临时的，一个不是
    if (aIsTemporary !== bIsTemporary) {
      // 已完成任务：临时任务排在正常任务后面
      if (a.done && b.done) {
        return aIsTemporary ? 1 : -1;
      }
      // 未完成任务：正常任务排在临时任务前面
      if (!a.done && !b.done) {
        return aIsTemporary ? 1 : -1;
      }
    }
  }

  // 1. 已完成的任务排在最后
  if (a.done !== b.done) {
    return a.done ? 1 : -1;
  }

  // 2. 有时间的任务优先于无时间的任务
  if (timeA.hasTime !== timeB.hasTime) {
    return timeA.hasTime ? -1 : 1;
  }

  // 3. 都没有时间，按创建时间排序（或保持原顺序）
  if (!timeA.hasTime && !timeB.hasTime) {
    const createdA = new Date(a.createdAt);
    const createdB = new Date(b.createdAt);
    return createdA.getTime() - createdB.getTime();
  }

  // 4. 都有时间，按主要时间排序
  if (timeA.primaryTime && timeB.primaryTime) {
    const primaryDiff = timeA.primaryTime.getTime() - timeB.primaryTime.getTime();

    // 主要时间相同，比较次要时间
    if (primaryDiff === 0) {
      if (timeA.secondaryTime && timeB.secondaryTime) {
        return timeA.secondaryTime.getTime() - timeB.secondaryTime.getTime();
      }
      // 有次要时间的排在前面
      if (timeA.secondaryTime !== timeB.secondaryTime) {
        return timeA.secondaryTime ? -1 : 1;
      }
    }

    return primaryDiff;
  }

  return 0;
};

/**
 * 通用的任务排序函数
 * 按时间字段排序任务列表
 */
export const sortTasksByTime = (tasks: Task[], temporaryVisibleTasks?: Set<string>): Task[] => {
  return [...tasks].sort((a, b) => compareTaskTime(a, b, temporaryVisibleTasks));
};

/**
 * 按日期分组并排序任务
 * 返回按日期分组的任务，每组内部已排序
 */
export const groupAndSortTasksByDate = (tasks: Task[]): Record<string, Task[]> => {
  const groups: Record<string, Task[]> = {};
  
  tasks.forEach(task => {
    const timeInfo = getTaskTimeInfo(task);
    let dateKey: string;
    
    if (timeInfo.hasTime && timeInfo.primaryTime) {
      // 使用主要时间的日期作为分组键
      dateKey = timeInfo.primaryTime.toDateString();
    } else {
      // 无时间任务分到特殊组
      dateKey = 'no-time';
    }
    
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(task);
  });
  
  // 对每个组内的任务进行排序
  Object.keys(groups).forEach(dateKey => {
    groups[dateKey] = sortTasksByTime(groups[dateKey]);
  });
  
  return groups;
};

/**
 * 获取任务的紧凑时间显示文本
 * 智能显示 startAt、cost、dueAt 三个时间信息，最大化信息密度
 */
export const getTaskCompactTimeText = (task: Task): string => {
  const timeInfo = getTaskTimeInfo(task);

  if (!timeInfo.hasTime) {
    return '';
  }

  const parts: string[] = [];

  // 1. 开始时间 (startAt)
  if (task.startAt) {
    const startTime = new Date(task.startAt);
    const timeStr = startTime.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    parts.push(timeStr);
  }

  // 2. 时长 (cost)
  if (task.cost && task.cost > 0) {
    const minutes = Math.floor(task.cost / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      const durationStr = remainingMinutes > 0 ? `${hours}h${remainingMinutes}m` : `${hours}h`;
      parts.push(durationStr);
    } else {
      parts.push(`${minutes}m`);
    }
  }

  // 3. 截止时间 (dueAt) - 只有当它与开始时间不同时才显示
  if (task.dueAt && task.dueAt !== task.startAt) {
    const dueTime = new Date(task.dueAt);
    const dueTimeStr = dueTime.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    // 如果有开始时间，用箭头表示时间范围
    if (task.startAt) {
      parts.push(`→${dueTimeStr}`);
    } else {
      // 只有截止时间，用 due: 前缀
      parts.push(`due:${dueTimeStr}`);
    }
  }

  return parts.join(' ');
};

/**
 * 获取任务的显示时间文本 (保持向后兼容)
 * 用于在UI中显示时间信息
 */
export const getTaskDisplayTimeText = (task: Task): string => {
  return getTaskCompactTimeText(task);
};

/**
 * 检查任务是否过期
 */
export const isTaskOverdue = (task: Task): boolean => {
  if (task.done) return false;
  
  const timeInfo = getTaskTimeInfo(task);
  if (!timeInfo.hasTime || !timeInfo.primaryTime) return false;
  
  const now = new Date();
  return timeInfo.primaryTime < now;
};

/**
 * 获取任务的时间状态
 */
export const getTaskTimeStatus = (task: Task): 'overdue' | 'today' | 'upcoming' | 'none' => {
  if (task.done) return 'none';
  
  const timeInfo = getTaskTimeInfo(task);
  if (!timeInfo.hasTime || !timeInfo.primaryTime) return 'none';
  
  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const taskDate = new Date(timeInfo.primaryTime);
  taskDate.setHours(0, 0, 0, 0);
  
  if (timeInfo.primaryTime < now) return 'overdue';
  if (taskDate.getTime() === today.getTime()) return 'today';
  return 'upcoming';
};
